import { formatDistanceToNow, parseISO } from 'date-fns';
import { INTEGRATION_CONFIG_KEYS } from '@onward-delivery/core';
import { INTEGRATION_CONFIG_FIELDS, INTEGRATION_TYPE_LABELS } from '@/components/CorporateUsers/modals/constants';

// Same rule IntegrationRow's type select applies to a default; a type without fields can't be entered here.
export const TMS_TYPES = Object.keys(INTEGRATION_CONFIG_FIELDS).filter(
    (type) => INTEGRATION_CONFIG_KEYS[type]?.selectable
);

// integration_overrides_status_check allows exactly these.
const TMS_STATUSES = {
    PENDING: { label: 'Waiting for first sync', variant: 'warning' },
    NEEDS_INFO: { label: 'Needs info', variant: 'warning' },
    COMPLETE: { label: 'Connected', variant: 'primary' },
    ERROR: { label: 'Error', variant: 'destructive' },
};

export const tmsStatusOf = (status) => {
    if (!status) return null;

    const tmsStatus = TMS_STATUSES[status];
    if (!tmsStatus) throw new Error(`Unknown integration status ${status}`);

    return tmsStatus;
};

export const typeLabelOf = (type) => INTEGRATION_TYPE_LABELS[type] || type;

export const lastSyncOf = (lastSuccessAt) =>
    lastSuccessAt ? formatDistanceToNow(parseISO(lastSuccessAt), { addSuffix: true }) : 'Never';

export const tmsMethodOf = (integration) => {
    if (integration.rec_isa_id || integration.send_isa_id) return 'EDI';
    if (integration.poll) return 'Polling';

    return 'API';
};

// The save endpoint rejects a config missing any of these, so they are what the card asks for.
export const requiredFieldLabelsOf = (type) => {
    const { required } = INTEGRATION_CONFIG_KEYS[type];
    const fields = INTEGRATION_CONFIG_FIELDS[type];

    return required.map((key) => {
        const field = fields.find((candidate) => candidate.key === key);
        if (!field) throw new Error(`${type} requires ${key}, which has no form field`);

        return field.label;
    });
};

// A MANUAL row is only a placeholder for a connection, and PC_MILER is configured on the shipper alone, so neither is
// an integration with the carrier. The view's integration_status leaves out MANUAL only.
const NOT_CARRIER_INTEGRATION_TYPES = ['MANUAL', 'PC_MILER'];

// Most urgent first: a shipper with several integrations shows the one that needs attention.
const STATUS_URGENCY = ['ERROR', 'NEEDS_INFO', 'PENDING', 'COMPLETE'];

const urgencyOf = (status) => {
    const index = STATUS_URGENCY.indexOf(status);
    return index === -1 ? STATUS_URGENCY.length : index;
};

/** Relationships whose integrations the org sets up: Flex Centers and Fulfillment Partners. Motor Carriers skip it. */
export const INTEGRATED_RELATIONSHIPS = ['OWNED', 'PARTNER'];

/**
 * One row per org shipper that has its own integration with the carrier: its integrations, the most urgent status,
 * and the latest sync. Sorted by shipper name.
 * @param {{ overrides: object[], carrierId: string, shippersById: Object<string, object> }} args `overrides` are the
 *   org shippers' integration_overrides rows
 */
export const shipperIntegrationRowsFor = ({ overrides, carrierId, shippersById }) => {
    const byShipper = {};

    for (const override of overrides || []) {
        const overrideCarrierId = override.carrier_id || override.middle_mile_carrier_id;
        if (overrideCarrierId !== carrierId || NOT_CARRIER_INTEGRATION_TYPES.includes(override.type)) continue;
        if (!shippersById[override.shipper_id]) continue;

        (byShipper[override.shipper_id] ??= []).push(override);
    }

    return Object.entries(byShipper)
        .map(([shipperId, integrations]) => {
            const [urgent] = [...integrations].sort((a, b) => urgencyOf(a.status) - urgencyOf(b.status));
            const syncs = integrations
                .map((integration) => integration.last_success_at)
                .filter(Boolean)
                .sort();

            return {
                shipper: shippersById[shipperId],
                integrations,
                types: integrations.map((integration) => integration.type),
                status: urgent.status,
                lastSuccessAt: syncs[syncs.length - 1] || null,
            };
        })
        .sort((a, b) => (a.shipper.business_name || '').localeCompare(b.shipper.business_name || ''));
};

/** Whether the carrier has an integration of any kind with the org: a TMS default, or one with a shipper. */
export const hasConnectedIntegration = (defaultIntegrations, shipperRows) =>
    shipperRows.length > 0 || defaultIntegrations.some((integration) => integration.type !== 'MANUAL');
